/**
 * Composable per Profit Centers autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { ProfitCenterAutocompleteResult } from '@/lib/models/profitCenter';
import { getProfitCentersAutocomplete } from '@/lib/autocompletes/profitCenters';

export function useProfitCentersAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const profitCenters = ref<ProfitCenterAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	// Usa withLoading iniettato dal parent, fallback su istanza locale se non fornito
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchProfitCenters(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getProfitCentersAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					profitCenters.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useProfitCentersAutocomplete.searchProfitCenters:', err);
				error('Errore durante il caricamento dei Profit! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					profitCenters.value = [];
				}
			}
		}, 'searchProfitCenters');
	}

	function clearProfitCenters(): void {
		profitCenters.value = [];
	}

	return {
		profitCenters: readonly(profitCenters),
		searchProfitCenters,
		clearProfitCenters,
	};
}
